/* @flow */
import React, { createContext, type Node, PureComponent } from 'react'
import Cookie from '../components/utils/Cookie'
import ExternalService from '../components/utils/ExternalService'
import request from '../components/utils/Request'
import { getAdsPersonalizationConsent } from '../../lib/utils/didomiConsent'

type EmailProviderProps = {|
  children: Node,
  options: {
    proxyOptions: Object,
    requiredCookies: Array<string>,
    serviceUrlPattern: string,
    xhrLogoutUri: string
  }
|}

type EmailProviderState = {|
  inbox: Object,
  isLoading: boolean
|}

const RELOAD_INTERVAL_MS = 120000

const EmailContext: Object = createContext()

const EmailConsumer = EmailContext.Consumer

class EmailProvider extends PureComponent<EmailProviderProps, EmailProviderState> {
  emailRefreshingInterval: IntervalID
  handleLogoutClick: (event: Object) => void
  constructor (props: EmailProviderProps) {
    super(props)

    this.state = {
      inbox: {},
      isLoading: false
    }

    this.handleLogoutClick = this.handleLogoutClick.bind(this)
  }

  componentDidMount () {
    this.loadInbox()
  }

  loadInbox () {
    const {
      options: {
        proxyOptions,
        requiredCookies,
        serviceUrlPattern
      }
    } = this.props
    if (this.shouldProcessInboxRequest(requiredCookies)) {
      const serviceHelper = new ExternalService(proxyOptions)
      const serviceUri = serviceHelper.getServiceUrl(serviceUrlPattern)
      this.loadInboxForFirstTime(serviceUri)
      this.startPeriodicallyLoadingInbox(serviceUri)
    }
  }

  shouldProcessInboxRequest (requiredCookies: Array<string>) {
    let shouldProcessInboxRequest = true
    if (requiredCookies && requiredCookies.length > 0) {
      const cookieHandler = new Cookie()
      if (Object.keys(cookieHandler.cookieJar).length > 0) {
        for (const cookieName of requiredCookies) {
          if (!cookieHandler.isSet(cookieName)) {
            shouldProcessInboxRequest = false
            break
          }
        }
      }
    } else {
      shouldProcessInboxRequest = false
    }
    return shouldProcessInboxRequest
  }

  loadInboxForFirstTime (serviceUri: string) {
    this.setState({
      isLoading: true
    })
    this.sendRequest(serviceUri).then(inbox => {
      this.setState({
        inbox,
        isLoading: false
      })
      window.localStorage.setItem('tracked', 'true')
      const {
        'user-service': userService,
        'user-uuid': userUuid
      } = inbox
      if (userUuid && userService) {
        window.didomiOnReady.push(() => {
          if (getAdsPersonalizationConsent()) {
            this.setCpexTuple(userUuid, userService)
          }
        })
      }
    }).catch(() => {
      this.setState({
        isLoading: false
      })
    })
  }

  startPeriodicallyLoadingInbox (serviceUri: string) {
    this.emailRefreshingInterval = setInterval(() => {
      this.sendRequest(serviceUri).then(inbox => {
        this.setState({
          inbox
        })
      }).catch(() => {
      })
    }, RELOAD_INTERVAL_MS)
  }

  sendRequest (serviceUri: string): Promise<Object> {
    return new Promise((resolve, reject) => {
      const config = {
        url: serviceUri,
        withCredentials: true,
        proxy: false
      }

      request(config).then(response => {
        const inbox = (typeof response.data === 'object') ? response.data : {}
        return resolve(inbox)
      }).catch(error => {
        return reject(error)
      })
    })
  }

  handleLogoutClick (event: Object) {
    this.setState({
      isLoading: true
    })

    const {
      options: {
        xhrLogoutUri
      }
    } = this.props
    clearInterval(this.emailRefreshingInterval)
    const eventTargetUri = event.target.getAttribute('href')
    this.sendLogoutRequest(xhrLogoutUri).then(() => {
      this.loadInbox()
    }).catch(() => {
      document.location.href = eventTargetUri
      this.setState({
        isLoading: false
      })
    })
  }

  sendLogoutRequest (logoutUri: string): Promise<void> {
    return new Promise((resolve, reject) => {
      request({
        url: logoutUri,
        withCredentials: true
      }).then(() => {
        return resolve()
      }).catch(error => {
        return reject(error)
      })
    })
  }

  setCpexTuple (userUuid: string, userService: string) {
    if (window) {
      window.eco = {
        token: userUuid,
        service: userService
      }
    }
  }

  render () {
    const {
      props: {
        children
      },
      state
    } = this

    const contextValue = Object.assign({}, state, {
      handleLogoutClick: this.handleLogoutClick
    })

    return (
      <EmailContext.Provider value={contextValue}>
        { children }
      </EmailContext.Provider>
    )
  }
}

export {
  EmailProvider,
  EmailConsumer
}
